import React, { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import RespiratoryRate from "../../icons/RespiratoryRate";
import {
  pickSpirometryResult,
  SPIROMETRY_PREDICTION_SOURCES,
} from "../../../utils/results/spirometry";
import MetricValue from "../MetricValue";
import QualityValue from "../QualityValue";
import { SignalChart } from "../../charts/SignalChart";
import FlowLoopChart from "../../charts/FlowLoopChart";
import SignalQuality from "../../icons/SignalQuality";
import { trackEvent } from "../../../utils/analytics/track";
import { useUserWebAppPresentationFeatures } from "../../../hooks/useUserWebAppPresentationFeatures";
import {
  formatRecordingIdDateParts,
  parseTimestampFromRecordingId,
} from "../../../utils/results/recordingIdTime";
import LungIcon from "../../icons/LungIcon";
import Spacer from "../../Spacer";
import {
  ResultSummaryRow,
  ResultSummarySection,
} from "../ResultSummarySection";
import ResultQualityNotice from "../ResultQualityNotice";
import ResultViewScaffold from "../ResultViewScaffold";
import ResultCard from "../../common/ResultCard";
import AveragedFlowLoopsSection from "../AveragedFlowLoopsSection";
import {
  getResultModeIcon,
  getResultModeLabel,
} from "../../../utils/results/resultModePresentation";
import {
  buildSpirometryAdditionalMetricsFields,
  buildSpirometryAdditionalMetricsRecord,
  getSpiroQualityMessage,
  getSpirometryQualityHintKeys,
  isSpirometryResultReady,
} from "../../../utils/results/spirometryView";
import { getByPath } from "../../../utils/results/metricsKVTableHelpers";
import {
  pickAveragedFlowLoop,
  pickPrimaryFlowLoopRecord,
} from "../../../utils/results/flowLoops";
import {
  getPointSeriesMaxSecs,
  getTimeSeriesMaxSecs,
  normaliseTimeValuePoints,
} from "../../../utils/results/viewTimeSeries";

function SpirometryResultViewImpl({
  data,
  docId,
  variant,
  footer = null,
  predictionSource = SPIROMETRY_PREDICTION_SOURCES.LUNGDX,
}) {
  const { t, i18n } = useTranslation();
  const { resultFeatures = {}, elementFeatures = {} } =
    useUserWebAppPresentationFeatures();
  const showSignalQuality = Boolean(resultFeatures.signalQuality);
  const showSpirometryQuality = Boolean(resultFeatures.spirometryQuality);
  const showFev1 = Boolean(resultFeatures.fev1);
  const showFvc = Boolean(resultFeatures.fvc);
  const showFev1Fvc = Boolean(resultFeatures.fev1Fvc);
  const showPef = Boolean(resultFeatures.pef);
  const showFlowLoops = Boolean(resultFeatures.flowLoops);
  const showRr = Boolean(resultFeatures.rr);
  const showRespiratorySignalChart = Boolean(
    elementFeatures.respiratorySignalChart,
  );
  const showSpirometrySegmentChart = Boolean(
    elementFeatures.spirometrySegmentChart,
  );
  const showSpirometryMetrics = showFev1 || showFvc || showFev1Fvc || showPef;
  const showQualitySummary = showSignalQuality || showSpirometryQuality;
  const showTraceDetails =
    showRespiratorySignalChart || showSpirometrySegmentChart || showFlowLoops;

  const {
    fev1,
    fvc,
    fev1FvcRatio,
    pef,
    rr,
    signalQuality,
    spirometryQuality,
    spirometryQualityChecks,
    zeroedMetrics,
    additionalMetrics,
    processedSignal,
  } = useMemo(
    () => pickSpirometryResult(data, { predictionSource }),
    [data, predictionSource],
  );

  const recordingTime = useMemo(
    () =>
      formatRecordingIdDateParts(
        docId,
        i18n.resolvedLanguage || i18n.language || "en-GB",
      ),
    [docId, i18n.resolvedLanguage, i18n.language],
  );

  const qualityMsg = useMemo(
    () =>
      getSpiroQualityMessage(
        { signalQuality, spirometryQuality, zeroedMetrics },
        t,
      ),
    [signalQuality, spirometryQuality, zeroedMetrics, t],
  );

  const signalQualityIsPass = signalQuality === "pass";
  const resultQualityIsDisabled = Boolean(qualityMsg || !signalQualityIsPass);
  const resultQualityNotice =
    qualityMsg ||
    (!signalQualityIsPass
      ? {
          title: t("result.spiro.qualityFailTitle", "Low signal quality."),
          subtitle: t("result.spiro.qualityFailSubtitle", "Please try again."),
        }
      : null);

  const resultQualityHints = useMemo(
    () =>
      getSpirometryQualityHintKeys(spirometryQualityChecks).map((checkKey) =>
        t(`result.spiro.qualityHints.items.${checkKey}`),
      ),
    [spirometryQualityChecks, t],
  );

  const recordingTimestamp = useMemo(
    () => parseTimestampFromRecordingId(docId),
    [docId],
  );

  const rrValue = useMemo(() => {
    if (typeof rr?.average === "number" && Number.isFinite(rr.average)) {
      return rr.average;
    }
    if (typeof rr?.mean === "number" && Number.isFinite(rr.mean)) {
      return rr.mean;
    }
    return null;
  }, [rr]);

  const detrendedSignalPoints = useMemo(() => {
    return normaliseTimeValuePoints(
      processedSignal?.detrendedSignalPoints || [],
    );
  }, [processedSignal]);

  const detrendedSignalMaxSecs = useMemo(() => {
    return getTimeSeriesMaxSecs(
      processedSignal?.timeSeconds,
      getPointSeriesMaxSecs(detrendedSignalPoints, 1),
    );
  }, [processedSignal, detrendedSignalPoints]);

  const spirometryMaxSecs = useMemo(() => {
    return getTimeSeriesMaxSecs(processedSignal?.spirometryTimeSeconds, 60);
  }, [processedSignal]);

  const normalisedSignalTracePoints = useMemo(() => {
    return normaliseTimeValuePoints(
      processedSignal?.spirometrySignalTracePoints || [],
    );
  }, [processedSignal]);
  const flowLoopRecord = useMemo(() => pickPrimaryFlowLoopRecord(data), [data]);
  const averagedFlowLoop = useMemo(() => pickAveragedFlowLoop(data), [data]);

  const additionalMetricsRecord = useMemo(
    () =>
      buildSpirometryAdditionalMetricsRecord(
        additionalMetrics,
        rr,
        signalQuality,
        spirometryQuality,
      ),
    [additionalMetrics, rr, signalQuality, spirometryQuality],
  );

  const additionalMetricsFields = useMemo(
    () => buildSpirometryAdditionalMetricsFields(t),
    [t],
  );

  // const mpmDetrendedSignal = useMemo(
  //   () => pickCardiopulmonaryDetrendedSignal(data),
  //   [data],
  // );

  const details = showTraceDetails ? (
    <section className="w-full p-4 pt-2 flex flex-col gap-4">
      {showRespiratorySignalChart ? (
        <ResultCard
          icon={getResultModeIcon(variant)}
          title={t("result.spiro.signalFullDetrended", "Signal")}
        >
          <SignalChart
            smoothSignal={detrendedSignalPoints}
            rawSignal={[]}
            peaks={[]}
            troughs={[]}
            maxSecs={detrendedSignalMaxSecs}
            height={196}
          />
        </ResultCard>
      ) : null}

      {showSpirometrySegmentChart ? (
        <ResultCard
          icon={getResultModeIcon(variant)}
          title={t("result.spiro.signalSpiroSegment", "Spirometry Segment")}
        >
          <SignalChart
            smoothSignal={normalisedSignalTracePoints}
            rawSignal={[]}
            peaks={[]}
            troughs={[]}
            maxSecs={spirometryMaxSecs}
            height={220}
            tickStepSize={0.5}
          />
        </ResultCard>
      ) : null}

      {showFlowLoops ? (
        <>
          <ResultCard
            icon={getResultModeIcon(variant)}
            title={t("result.spiro.spiroFlowLoop", "Spirometry Flow Loop")}
          >
            <FlowLoopChart
              title={null}
              points={processedSignal?.spirometryFlowLoop}
              xTitle={t("result.spiro.volumeProxy", "Volume")}
              yTitle={t("result.spiro.flowProxy", "Flow")}
              height={280}
            />
          </ResultCard>

          {averagedFlowLoop ? (
            <ResultCard
              icon={getResultModeIcon(variant)}
              title={t(
                averagedFlowLoop.mode === "windowed"
                  ? "results.flowLoops.averagedSectionTitlePlural"
                  : "results.flowLoops.averagedSectionTitle",
                averagedFlowLoop.mode === "windowed"
                  ? "Average Flow-Volume Loops"
                  : "Average Flow-Volume Loop",
              )}
            >
              <AveragedFlowLoopsSection flowLoopSource={flowLoopRecord} />
            </ResultCard>
          ) : null}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {additionalMetricsFields.map((field) => {
              const value = field.fmt(
                getByPath(additionalMetricsRecord, field.path),
                additionalMetricsRecord,
              );

              return (
                <ResultCard
                  key={field.path}
                  icon={getResultModeIcon(variant)}
                  title={t(field.i18nKey, { defaultValue: field.label })}
                >
                  <p className="title-text text-primary">
                    {value}
                    {field.unit && value !== "--" ? (
                      <span className="muted-text"> {field.unit}</span>
                    ) : null}
                  </p>
                </ResultCard>
              );
            })}
          </div>
        </>
      ) : null}
    </section>
  ) : null;

  return (
    <ResultViewScaffold
      title={getResultModeLabel(variant, t)}
      icon={getResultModeIcon(variant)}
      recordingTime={recordingTime}
      details={details}
      footer={footer}
    >
      <Spacer />
      {showQualitySummary ? (
        <ResultSummarySection
          icon={<SignalQuality />}
          title={t("result.qualityTitle", "Recording Quality")}
          disabled={resultQualityIsDisabled}
        >
          {showSignalQuality ? (
            <ResultSummaryRow
              label={t("result.spiro.signalQuality", "Signal Quality")}
              value={
                <QualityValue
                  status={signalQuality}
                  recordingTimestamp={recordingTimestamp}
                />
              }
              disabled={resultQualityIsDisabled}
            />
          ) : null}
          {showSpirometryQuality ? (
            <ResultSummaryRow
              label={t("result.spiro.spirometryQuality", "Spirometry Quality")}
              value={
                <QualityValue
                  status={spirometryQuality}
                  recordingTimestamp={recordingTimestamp}
                />
              }
              disabled={resultQualityIsDisabled}
            />
          ) : null}

          <ResultQualityNotice
            messages={resultQualityNotice ? [resultQualityNotice] : []}
            hints={resultQualityHints}
            hintsTitle={t("result.spiro.qualityHints.title", "What to improve")}
            linkTo="/guidance/spirometry"
            ctaLabel={t("result.spiro.guidanceCta", "Tips")}
            onLinkClick={() =>
              trackEvent("spirometry_result_click_guidance", {
                signal_quality: signalQuality,
                spirometry_quality: spirometryQuality,
                zeroed_metrics: zeroedMetrics,
              })
            }
          />
        </ResultSummarySection>
      ) : null}
      {showSpirometryMetrics ? (
        <ResultSummarySection
          icon={<RespiratoryRate />}
          title={t("result.spiro.spirometryTitle", "Spirometry")}
          disabled={resultQualityIsDisabled}
        >
          {showFev1 ? (
            <ResultSummaryRow
              label={t("result.spiro.fev1", "FEV1")}
              value={
                <MetricValue
                  metric="fev1"
                  value={fev1}
                  unit={t("result.spiro.FEV1Unit", "L")}
                  recordingTimestamp={recordingTimestamp}
                />
              }
              disabled={resultQualityIsDisabled}
            />
          ) : null}
          {showFvc ? (
            <ResultSummaryRow
              label={t("result.spiro.fvc", "FVC")}
              value={
                <MetricValue
                  metric="fvc"
                  value={fvc}
                  unit={t("result.spiro.FVCUnit", "L")}
                  recordingTimestamp={recordingTimestamp}
                />
              }
              disabled={resultQualityIsDisabled}
            />
          ) : null}
          {showFev1Fvc ? (
            <ResultSummaryRow
              label={t("result.spiro.fev1FvcRatio", "FEV1/FVC")}
              value={
                <MetricValue
                  metric="fev1FvcRatio"
                  value={fev1FvcRatio}
                  recordingTimestamp={recordingTimestamp}
                />
              }
              disabled={resultQualityIsDisabled}
            />
          ) : null}
          {showPef ? (
            <ResultSummaryRow
              label={t("result.spiro.pef", "PEF")}
              value={
                <MetricValue
                  metric="pef"
                  value={pef}
                  unit={t("result.spiro.PEFUnit", "L/min")}
                  recordingTimestamp={recordingTimestamp}
                />
              }
              disabled={resultQualityIsDisabled}
            />
          ) : null}
        </ResultSummarySection>
      ) : null}

      {showRr ? (
        <ResultSummarySection
          icon={<LungIcon />}
          title={t("result.spiro.respiratory", "Respiratory")}
          disabled={resultQualityIsDisabled}
        >
          <ResultSummaryRow
            label={t("result.resp.rrLongLabel", "Respiratory Rate")}
            value={
              <MetricValue
                metric="rr"
                value={rrValue}
                unit={t("result.resp.rrUnit", "bpm")}
                recordingTimestamp={recordingTimestamp}
              />
            }
            disabled={resultQualityIsDisabled}
          />
        </ResultSummarySection>
      ) : null}
    </ResultViewScaffold>
  );
}

const SpirometryResultView = memo(SpirometryResultViewImpl);

SpirometryResultView.isReady = isSpirometryResultReady;

export default SpirometryResultView;
